import { useState, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";

/**
 * ExtractionProgressCard
 * Displays extraction status across multiple sections during document ingestion.
 * Features:
 * - 3 animated gradient dots (blue, purple, light blue)
 * - Title: "Extracting information"
 * - Subtitle: "Processing your document across {N} sections"
 * - Gradient progress bar (blue to purple) that advances as sections complete non-sequentially
 * - Right-aligned percentage indicator
 */
export default function ExtractionProgressCard({
  sections = null,
  totalSections = null,
  active = true,
  externalProgress = null,
  onCancel = null,
}) {
  const count = Math.max(
    1,
    Array.isArray(sections) && sections.length > 0
      ? sections.length
      : Number(totalSections) > 0
      ? Number(totalSections)
      : 1
  );
  const sectionStep = 100 / count;

  // Initialize progress with an initial ingestion offset
  const [progress, setProgress] = useState(
    externalProgress != null ? externalProgress : Math.min(8, sectionStep)
  );

  const completedCountRef = useRef(1);

  useEffect(() => {
    if (externalProgress != null) {
      setProgress(externalProgress);
      return;
    }

    if (!active) {
      setProgress(100);
      return;
    }

    // Reset progress on extraction start
    completedCountRef.current = 1;
    setProgress(Math.min(8, sectionStep));

    let timer = null;

    const scheduleNextSectionCompletion = () => {
      // Randomize interval between 500ms and 1500ms to simulate asynchronous section completions
      const nextDelay = Math.floor(Math.random() * 1000) + 600;

      timer = setTimeout(() => {
        completedCountRef.current += 1;
        const currentCompleted = completedCountRef.current;
        const nextPct = Math.min(94, (currentCompleted / count) * 100);

        setProgress((prev) => Math.max(prev, nextPct));

        if (currentCompleted < count && active) {
          scheduleNextSectionCompletion();
        }
      }, nextDelay);
    };

    scheduleNextSectionCompletion();

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [active, externalProgress, count, sectionStep]);

  const displayPct = Math.min(100, Math.max(0, Math.round(progress)));

  return (
    <Box
      sx={{
        mb: 2,
        p: { xs: "16px 14px", sm: "20px 20px", md: "24px 28px" },
        bgcolor: "#FFFFFF",
        borderRadius: { xs: "10px", sm: "12px" },
        border: "1px solid #E5E7EB",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.04)",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      <style>{`
        @keyframes extractionDotPulse {
          0%, 100% {
            transform: scale(0.8);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.25);
            opacity: 1;
          }
        }
      `}</style>

      {/* Top Header Row: 3 Animated Dots + Optional Cancel */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: { xs: 1, sm: 1.5 },
          minWidth: 0,
        }}
      >
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: { xs: "5px", sm: "6px" } }}>
          <Box
            sx={{
              width: { xs: 6, sm: 7 },
              height: { xs: 6, sm: 7 },
              borderRadius: "50%",
              bgcolor: "#3B82F6", // blue
              animation: "extractionDotPulse 1.4s ease-in-out infinite",
              animationDelay: "0s",
            }}
          />
          <Box
            sx={{
              width: { xs: 6, sm: 7 },
              height: { xs: 6, sm: 7 },
              borderRadius: "50%",
              bgcolor: "#A855F7", // purple
              animation: "extractionDotPulse 1.4s ease-in-out infinite",
              animationDelay: "0.2s",
            }}
          />
          <Box
            sx={{
              width: { xs: 6, sm: 7 },
              height: { xs: 6, sm: 7 },
              borderRadius: "50%",
              bgcolor: "#60A5FA", // light blue
              animation: "extractionDotPulse 1.4s ease-in-out infinite",
              animationDelay: "0.4s",
            }}
          />
        </Box>

        {onCancel && (
          <Box
            component="button"
            type="button"
            onClick={onCancel}
            title="Cancel extraction"
            sx={{
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              fontFamily: "Lato, sans-serif",
              fontWeight: 600,
              fontSize: { xs: "12px", sm: "13px" },
              lineHeight: "18px",
              color: "#6B7280",
              px: "8px",
              py: "2px",
              borderRadius: "4px",
              transition: "color 0.15s ease, background-color 0.15s ease",
              "&:hover": {
                color: "#111827",
                bgcolor: "rgba(0, 0, 0, 0.05)",
              },
            }}
          >
            Cancel
          </Box>
        )}
      </Box>

      {/* Title & Subtitle */}
      <Typography
        sx={{
          fontFamily: "Lato, sans-serif",
          fontWeight: 700,
          fontSize: { xs: "15px", sm: "16px", md: "18px" },
          lineHeight: { xs: "22px", sm: "24px" },
          color: "#111827",
          mb: 0.5,
          wordBreak: "break-word",
        }}
      >
        Extracting information
      </Typography>
      <Typography
        sx={{
          fontFamily: "Lato, sans-serif",
          fontWeight: 400,
          fontSize: { xs: "12.5px", sm: "13px", md: "14px" },
          lineHeight: { xs: "18px", sm: "20px" },
          color: "#6B7280",
          mb: { xs: 1.5, sm: 2 },
          wordBreak: "break-word",
        }}
      >
        Processing your document across {count} {count === 1 ? "section" : "sections"}
      </Typography>

      {/* Gradient Progress Bar */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 7, sm: 8 },
          bgcolor: "#E5E7EB",
          borderRadius: "999px",
          overflow: "hidden",
          position: "relative",
          mb: { xs: 1, sm: 1.25 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            height: "100%",
            borderRadius: "999px",
            background:
              "linear-gradient(90deg, #1D4ED8 0%, #3B82F6 25%, #6366F1 50%, #8B5CF6 75%, #A855F7 100%)",
            width: `${displayPct}%`,
            transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </Box>

      {/* Bottom Info Row: Percentage on right (omitting section X of Y as requested) */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontWeight: 700,
            fontSize: { xs: "13px", sm: "14px" },
            lineHeight: "20px",
            color: "#374151",
          }}
        >
          {displayPct}%
        </Typography>
      </Box>
    </Box>
  );
}
